// PFMEA Editor Page - Full CRUD with inline editing, workflow, and audit trail
// Based on Magnus-Core FmeaEditorPage pattern
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileSpreadsheet,
    X,
    Maximize2,
    CheckCircle,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import clsx from 'clsx';
import {
    ChangeRecord,
    getPfmeaItems,
    calculateRPN,
} from '../services/pfmeaService';
import { PfmeaItem } from '../types';

import SmartSuggestPanel from '../components/SmartSuggestPanel';

import CoachingTipsPanel from '../components/CoachingTipsPanel';
import {
    EditableField,
    applyFilters,
    sortItems,
    calculateStatusCounts,
    transformContextItems,
    FilterState,
} from '../components/PfmeaEditorHelpers';
import PfmeaTable from '../components/PfmeaEditor/PfmeaTable';
import PfmeaToolbar from '../components/PfmeaEditor/PfmeaToolbar';
import PfmeaFilters from '../components/PfmeaEditor/PfmeaFilters';
import { PfmeaLayoutSettingsPanel, PfmeaLayoutSettings, DEFAULT_PFMEA_LAYOUT_SETTINGS } from '../components/PfmeaEditor/LayoutSettingsPanel';
import { useDfmeaImport } from '../hooks/useDfmeaImport';
import { usePfmeaHandlers } from '../hooks/usePfmeaHandlers';

interface EditingCell {
    id: string;
    field: EditableField;
}

interface CellPopout {
    id: string;
    field: EditableField;
    value: string;
    label: string;
}

interface RevisionModal {
    id: string;
    field: EditableField;
    oldValue: string;
    newValue: string;
}

const PfmeaEditorPage: React.FC = () => {
    const { theme } = useTheme();
    const { hasPermission } = useAuth();
    const {
        activeProject,
        activeProjectName,
        pfmeaItems: contextPfmeaItems,
        isFullScreen,
        // CRUD functions - will use for backend sync when implemented
        // addPfmeaItem: contextAddPfmeaItem,
        // updatePfmeaItem: contextUpdatePfmeaItem,
        // deletePfmeaItem: contextDeletePfmeaItem,
    } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // Data State - use context items
    const [items, setItems] = useState<PfmeaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // UI State
    const [viewMode, setViewMode] = useState<'all' | 'draft' | 'review' | 'approved'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [filter4M, setFilter4M] = useState<'all' | 'Man' | 'Machine' | 'Material' | 'Method'>('all');
    const [filterPriority, setFilterPriority] = useState<'all' | 'H' | 'M' | 'L'>('all');
    const [filterSC, setFilterSC] = useState<'all' | 'CC' | 'SC' | 'none'>('all');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [sortField, setSortField] = useState<keyof PfmeaItem>('processStepNumber');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Editing State
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<PfmeaItem>>>({});
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedItemHistory, setSelectedItemHistory] = useState<ChangeRecord[]>([]);

    // Cell Popout Modal State (read/edit full text)
    const [cellPopout, setCellPopout] = useState<CellPopout | null>(null);
    const [popoutEditValue, setPopoutEditValue] = useState('');

    // Revision Control Modal State (initials + reason for change)
    const [revisionModal, setRevisionModal] = useState<RevisionModal | null>(null);
    const [revisionInitials, setRevisionInitials] = useState('');
    const [revisionReason, setRevisionReason] = useState('');

    // SmartSuggest AI Panel State
    const [showSmartSuggest, setShowSmartSuggest] = useState(false);
    const [selectedProcessStep, setSelectedProcessStep] = useState<{
        name: string;
        number: string;
        function: string;
    } | null>(null);

    // Coaching Tips Panel State (no LLM - static tips only)
    const [showCoachingTips, setShowCoachingTips] = useState(true);
    const [activeEditingField, setActiveEditingField] = useState<EditableField | null>(null);
    const [selectedItemForCoaching, setSelectedItemForCoaching] = useState<PfmeaItem | null>(null);

    // Layout Settings State
    const [layoutSettings, setLayoutSettings] = useState<PfmeaLayoutSettings>(DEFAULT_PFMEA_LAYOUT_SETTINGS);
    const [layoutSettingsOpen, setLayoutSettingsOpen] = useState(false);

    // Transform context items using extracted utility
    const transformedContextItems = useMemo(() => {
        if (contextPfmeaItems && contextPfmeaItems.length > 0) {
            return transformContextItems(contextPfmeaItems, activeProjectName || '');
        }
        return null;
    }, [contextPfmeaItems, activeProjectName]);

    const loadData = useCallback(async () => {
        if (!activeProjectName) return;
        setLoading(true);
        try {
            const data = await getPfmeaItems(activeProjectName);
            setItems(data);
        } catch (err: unknown) {
            console.error('Error loading PFMEA items:', err);
            // Context items should already be set from useEffect
        } finally {
            setLoading(false);
        }
    }, [activeProjectName]);

    // Load data from context when it changes
    useEffect(() => {
        if (transformedContextItems) {
            setItems(transformedContextItems);
            setLoading(false);
        } else if (activeProjectName) {
            // Try to load from API if context is empty
            loadData();
        } else {
            setLoading(false);
        }
    }, [transformedContextItems, activeProjectName, loadData]);

    // SC/CC Import from DFMEA (via LinkedDataPage → sessionStorage)
    // SC/CC Import from DFMEA (via LinkedDataPage → sessionStorage)
    useDfmeaImport(activeProjectName, setItems, setPendingChanges, setFilterSC);

    // Filtered and sorted items - using extracted utilities
    const filteredItems = useMemo(() => {
        const filterState: FilterState = { viewMode, filter4M, filterPriority, filterSC, searchTerm };
        const filtered = applyFilters(items, filterState);
        return sortItems(filtered, sortField, sortDirection);
    }, [items, viewMode, filter4M, filterPriority, filterSC, searchTerm, sortField, sortDirection]);

    // Status counts - using extracted utility
    const statusCounts = useMemo(() => calculateStatusCounts(items), [items]);

    // Handlers
    const handleSort = (field: keyof PfmeaItem) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const {
        handleCellChange, handleCellClick, handleCellExpand, handlePopoutSave,
        handleRevisionCommit, handleRevisionSkip,
        handleAddRow, handleDuplicateRow, handleDeleteSelected, handleSave,
        handleSubmitForReview, handleApprove, handleReject,
        toggleSelectAll, toggleSelect, handleViewHistory, handleSmartSuggestClick,
        handleSmartSuggestAdd
    } = usePfmeaHandlers(
        items,
        setItems,
        setPendingChanges,
        activeProjectName,
        selectedIds,
        setSelectedIds,
        setSaving,
        cellPopout,
        setCellPopout,
        popoutEditValue,
        setPopoutEditValue,
        revisionModal,
        setRevisionModal,
        revisionInitials,
        setRevisionInitials,
        revisionReason,
        setRevisionReason,
        setSelectedItemForCoaching,
        hasPermission,
        setEditingCell,
        setActiveEditingField,
        setSelectedItemHistory,
        setShowHistoryModal,
        filteredItems,
        setShowSmartSuggest,
        showSmartSuggest,
        selectedProcessStep,
        setSelectedProcessStep
    );

    const handleCellBlur = () => {
        setEditingCell(null);
    };









    // No project selected
    if (!activeProject || activeProject === 'Select Project') {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <FileSpreadsheet size={64} className="text-slate-300 mb-4" />
                <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                    No Project Selected
                </h2>
                <p className="text-slate-500 mb-4">Select a project from the Projects page to start editing PFMEA items.</p>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                    Go to Projects
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className={clsx('text-2xl font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <FileSpreadsheet className="text-primary-500" />
                        PFMEA Editor
                    </h1>
                    <p className="text-slate-500 text-sm">
                        {activeProject} · {filteredItems.length} of {items.length} items
                        {Object.keys(pendingChanges).length > 0 && (
                            <span className="ml-2 text-amber-500">
                                ({Object.keys(pendingChanges).length} unsaved changes)
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {/* Toolbar */}
            <PfmeaToolbar
                isDark={isDark}
                viewMode={viewMode}
                setViewMode={setViewMode}
                statusCounts={statusCounts}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                hasActiveFilters={filter4M !== 'all' || filterPriority !== 'all' || filterSC !== 'all'}
                showCoachingTips={showCoachingTips}
                setShowCoachingTips={setShowCoachingTips}
                showSmartSuggest={showSmartSuggest}
                onSmartSuggestClick={handleSmartSuggestClick}
                handleAddRow={handleAddRow}
                handleDuplicateRow={handleDuplicateRow}
                handleDeleteSelected={handleDeleteSelected}
                handleSave={handleSave}
                saving={saving}
                selectedIds={selectedIds}
                pendingChanges={pendingChanges}
                hasPermission={hasPermission}
                wrapText={layoutSettings.wrapText}
                setWrapText={(v) => setLayoutSettings(prev => ({ ...prev, wrapText: v }))}
                onOpenLayoutSettings={() => setLayoutSettingsOpen(true)}
            />

            {/* Filters Bar */}
            <PfmeaFilters
                isDark={isDark}
                showFilters={showFilters}
                filter4M={filter4M}
                setFilter4M={setFilter4M}
                filterPriority={filterPriority}
                setFilterPriority={setFilterPriority}
                filterSC={filterSC}
                setFilterSC={setFilterSC}
            />

            {/* Table */}
            <PfmeaTable
                items={items}
                filteredItems={filteredItems}
                loading={loading}
                isDark={isDark}
                selectedIds={selectedIds}
                sortField={sortField}
                sortDirection={sortDirection}
                toggleSelectAll={toggleSelectAll}
                handleSort={handleSort}
                toggleSelect={toggleSelect}
                editingCell={editingCell}
                pendingChanges={pendingChanges}
                handleCellClick={handleCellClick}
                handleCellChange={handleCellChange}
                handleCellBlur={handleCellBlur}
                hasPermission={hasPermission}
                handleSubmitForReview={handleSubmitForReview}
                handleApprove={handleApprove}
                handleReject={handleReject}
                handleViewHistory={handleViewHistory}
                calculateRPN={calculateRPN}
                handleCellExpand={handleCellExpand}
                layoutSettings={layoutSettings}
            />

            {/* Layout Settings Panel */}
            {layoutSettingsOpen && (
                <PfmeaLayoutSettingsPanel
                    settings={layoutSettings}
                    onSettingsChange={setLayoutSettings}
                    onClose={() => setLayoutSettingsOpen(false)}
                    availableColumns={[
                        { key: 'processStepNumber', label: 'Step #' },
                        { key: 'processStepName', label: 'Process Step' },
                        { key: 'processElement', label: '4M' },
                        { key: 'failureMode', label: 'Failure Mode' },
                        { key: 'failureEffectEnd', label: 'Effect' },
                        { key: 'failureCause', label: 'Cause' },
                        { key: 'severity', label: 'S' },
                        { key: 'specialCharacteristic', label: 'Class' },
                        { key: 'occurrence', label: 'O' },
                        { key: 'detection', label: 'D' },
                        { key: 'rpn', label: 'RPN' },
                        { key: 'actionPriority', label: 'AP' },
                        { key: 'preventionControls', label: 'Prevention' },
                        { key: 'detectionControls', label: 'Detection' },
                        { key: 'status', label: 'Status' },
                    ]}
                />
            )}

            {/* History Modal */}
            {showHistoryModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className={clsx(
                        'w-full max-w-2xl max-h-[80vh] overflow-auto rounded-xl p-6',
                        isDark ? 'bg-slate-800' : 'bg-white'
                    )}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                Change History
                            </h2>
                            <button onClick={() => setShowHistoryModal(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        {selectedItemHistory.length === 0 ? (
                            <p className="text-slate-500 text-center py-8">No changes recorded yet.</p>
                        ) : (
                            <div className="space-y-3">
                                {selectedItemHistory.map((record, i) => (
                                    <div key={i} className={clsx('p-3 rounded-lg', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-medium">{record.field}</span>
                                            <span className="text-slate-500">{new Date(record.timestamp).toLocaleString()}</span>
                                        </div>
                                        <div className="text-sm mt-1">
                                            <span className="text-red-500 line-through">{record.oldValue}</span>
                                            {' → '}
                                            <span className="text-green-500">{record.newValue}</span>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            by {record.user}
                                            {record.comment && ` • "${record.comment}"`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ============================================================ */}
            {/* CELL POPOUT MODAL - Read & Edit Full Text */}
            {/* ============================================================ */}
            {cellPopout && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className={clsx(
                        'w-full max-w-2xl max-h-[80vh] overflow-auto rounded-xl p-6 shadow-2xl',
                        isDark ? 'bg-slate-800' : 'bg-white'
                    )}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                <Maximize2 size={18} className="inline mr-2 text-primary-500" />
                                {cellPopout.label}
                            </h2>
                            <button onClick={() => setCellPopout(null)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>

                        {/* Full Text Display */}
                        <div className={clsx(
                            'p-4 rounded-lg mb-4 text-sm border',
                            isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'
                        )}>
                            <p className="text-xs text-slate-500 mb-1 font-semibold uppercase">Current Value:</p>
                            <p className={clsx('whitespace-pre-wrap', isDark ? 'text-slate-200' : 'text-slate-800')}>
                                {cellPopout.value || <span className="italic text-slate-400">Empty</span>}
                            </p>
                        </div>

                        {/* Edit Area */}
                        {hasPermission('pfmea.edit') && (
                            <div className="mb-4">
                                <label className={clsx(
                                    'block text-sm font-medium mb-2',
                                    isDark ? 'text-slate-300' : 'text-slate-700'
                                )}>
                                    Edit Value:
                                </label>
                                <textarea
                                    value={popoutEditValue}
                                    onChange={(e) => setPopoutEditValue(e.target.value)}
                                    className={clsx(
                                        'w-full p-3 rounded-lg border text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary-500',
                                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'
                                    )}
                                    placeholder="Enter or edit cell content..."
                                />
                            </div>
                        )}

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setCellPopout(null)}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-sm',
                                    isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                            >
                                Cancel
                            </button>
                            {hasPermission('pfmea.edit') && (
                                <button
                                    onClick={handlePopoutSave}
                                    className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
                                >
                                    Save & Log Revision
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================ */}
            {/* REVISION CONTROL MODAL - Initials + Reason for Change */}
            {/* ============================================================ */}
            {revisionModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
                    <div className={clsx(
                        'w-full max-w-md rounded-xl p-6 shadow-2xl',
                        isDark ? 'bg-slate-800' : 'bg-white'
                    )}>
                        <h2 className={clsx('text-lg font-bold mb-4', isDark ? 'text-white' : 'text-slate-800')}>
                            <CheckCircle size={18} className="inline mr-2 text-green-500" />
                            Verify Change
                        </h2>

                        {/* Change Summary */}
                        <div className={clsx(
                            'p-3 rounded-lg mb-4 text-sm border',
                            isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'
                        )}>
                            <p className="font-semibold text-xs text-slate-500 uppercase mb-1">Field: {revisionModal.field}</p>
                            <div className="flex items-start gap-2">
                                <span className="text-red-500 line-through text-xs flex-1 break-words">
                                    {revisionModal.oldValue || '(empty)'}
                                </span>
                                <span className="text-slate-400">→</span>
                                <span className="text-green-500 text-xs flex-1 break-words">
                                    {revisionModal.newValue || '(empty)'}
                                </span>
                            </div>
                        </div>

                        {/* Initials Input */}
                        <div className="mb-3">
                            <label className={clsx(
                                'block text-sm font-medium mb-1',
                                isDark ? 'text-slate-300' : 'text-slate-700'
                            )}>Your Initials (Sign-off) *</label>
                            <input
                                type="text"
                                value={revisionInitials}
                                onChange={(e) => setRevisionInitials(e.target.value)}
                                className={clsx(
                                    'w-full p-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                )}
                                placeholder="e.g. J.D."
                                maxLength={10}
                                autoFocus
                            />
                        </div>

                        {/* Reason for Change */}
                        <div className="mb-4">
                            <label className={clsx(
                                'block text-sm font-medium mb-1',
                                isDark ? 'text-slate-300' : 'text-slate-700'
                            )}>Reason for Change</label>
                            <textarea
                                value={revisionReason}
                                onChange={(e) => setRevisionReason(e.target.value)}
                                className={clsx(
                                    'w-full p-2 rounded-lg border text-sm h-20 focus:outline-none focus:ring-2 focus:ring-primary-500',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                )}
                                placeholder="Briefly describe the change reason..."
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleRevisionSkip}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-sm',
                                    isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                            >
                                Skip (Minor Edit)
                            </button>
                            <button
                                onClick={handleRevisionCommit}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                            >
                                <CheckCircle size={16} />
                                Verify & Log
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SmartSuggest AI Panel - Slide in from right */}
            {showSmartSuggest && selectedProcessStep && !isFullScreen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setShowSmartSuggest(false)}
                    />
                    {/* Panel */}
                    <div className="relative z-10">
                        <SmartSuggestPanel
                            processStepName={selectedProcessStep.name}
                            processStepNumber={selectedProcessStep.number}
                            processFunction={selectedProcessStep.function}
                            isDark={isDark}
                            existingFailureModes={items.map(item => item.failureMode).filter(Boolean)}
                            onAddSuggestion={handleSmartSuggestAdd}
                            onClose={() => setShowSmartSuggest(false)}
                        />
                    </div>
                </div>
            )}

            {/* Coaching Tips Panel - Always visible side panel if enabled */}
            {showCoachingTips && selectedItemForCoaching && !isFullScreen && (
                <div className="fixed right-4 bottom-4 z-40 w-80">
                    <CoachingTipsPanel
                        item={selectedItemForCoaching as any}
                        activeField={activeEditingField || undefined}
                        showValidation={true}
                        onPreferencesChange={(prefs) => console.log('Prefs updated:', prefs)}
                    />
                </div>
            )}
        </div>
    );
};

export default PfmeaEditorPage;
